/**
 * Лицевая панель коммутатора на экране входа.
 *
 * Рисунок, а не данные: 24 медных порта и 4 оптических. При открытии
 * страницы индикаторы портов загораются слева направо — так, как
 * поднимаются линки после включения коммутатора. Несколько портов
 * остаются тёмными: свободные, как на настоящем объекте.
 *
 * Анимация одна и проигрывается один раз. При системной настройке
 * уменьшенного движения индикаторы сразу горят (общее правило в global.css).
 */
import styles from './SwitchFaceplate.module.css';

const COLUMNS = 12;
const COL_W = 34;
const GROUP_GAP = 10;
const PORTS_X = 96;
const TOP_Y = 32;
const BOTTOM_Y = 72;
const JACK_W = 26;
const JACK_H = 22;

// Свободные порты — без линка
const DOWN = new Set([5, 12, 17, 22, 27, 28]);

// Начало волны и шаг между колонками, мс
const START = 350;
const STEP = 55;

/** Гнездо RJ45: прямоугольник с выступом под защёлку. В нижнем ряду
 *  гнёзда перевёрнуты, как на настоящих коммутаторах. */
function jackPath(x, y, flipped) {
  const w = JACK_W,
    h = JACK_H,
    tab = 10,
    depth = 4;
  const tx = x + (w - tab) / 2;
  if (!flipped) {
    return `M${x} ${y}H${x + w}V${y + h}H${tx + tab}V${y + h + depth}H${tx}V${y + h}H${x}Z`;
  }
  return `M${x} ${y}H${tx}V${y - depth}H${tx + tab}V${y}H${x + w}V${y + h}H${x}Z`;
}

function colX(c) {
  return PORTS_X + c * COL_W + (c >= COLUMNS / 2 ? GROUP_GAP : 0);
}

function Led({ x, y, w = 7, h = 3, port, delay }) {
  const up = !DOWN.has(port);
  return (
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx="1"
      className={up ? styles.ledUp : styles.ledOff}
      style={up ? { animationDelay: `${delay}ms` } : undefined}
    />
  );
}

export default function SwitchFaceplate() {
  const copper = [];
  for (let c = 0; c < COLUMNS; c++) {
    const x = colX(c);
    const delay = START + c * STEP;
    const top = 2 * c + 1;
    const bottom = top + 1;
    copper.push(
      <g key={c}>
        <text x={x + JACK_W / 2} y={TOP_Y - 13} className={styles.num}>
          {top}
        </text>
        <Led x={x + 2} y={TOP_Y - 8} port={top} delay={delay} />
        <path d={jackPath(x, TOP_Y, false)} className={styles.jack} />
        <path d={jackPath(x, BOTTOM_Y, true)} className={styles.jack} />
        <Led x={x + 2} y={BOTTOM_Y + JACK_H + 5} port={bottom} delay={delay + 25} />
        <text x={x + JACK_W / 2} y={BOTTOM_Y + JACK_H + 17} className={styles.num}>
          {bottom}
        </text>
      </g>
    );
  }

  // Оптические порты: два столбца по два, загораются последними
  const sfpX0 = colX(COLUMNS - 1) + COL_W + 22;
  const sfp = [25, 26, 27, 28].map((port, i) => {
    const col = Math.floor(i / 2);
    const row = i % 2;
    const x = sfpX0 + col * 40;
    const y = row === 0 ? TOP_Y + 2 : BOTTOM_Y + 2;
    return (
      <g key={port}>
        <rect x={x} y={y} width="32" height="18" rx="1.5" className={styles.cage} />
        <rect x={x + 4} y={y + 5} width="24" height="8" rx="1" className={styles.jack} />
        <Led
          x={x + 12}
          y={row === 0 ? y - 8 : y + 23}
          w={8}
          port={port}
          delay={START + COLUMNS * STEP + 120 + i * 60}
        />
        <text x={x + 16} y={row === 0 ? y - 13 : y + 37} className={styles.num}>
          {port}
        </text>
      </g>
    );
  });

  return (
    <svg
      className={styles.faceplate}
      viewBox="0 0 640 128"
      role="img"
      aria-label="Лицевая панель коммутатора"
    >
      {/* Крепёжные уши с отверстиями под винты */}
      <rect x="0" y="10" width="26" height="108" rx="3" className={styles.ear} />
      <rect x="614" y="10" width="26" height="108" rx="3" className={styles.ear} />
      {[
        [13, 30],
        [13, 98],
        [627, 30],
        [627, 98],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4.5" className={styles.screw} />
      ))}

      <rect x="22" y="4" width="596" height="120" rx="6" className={styles.body} />

      {/* Индикаторы питания и состояния */}
      <circle cx="46" cy="42" r="3.5" className={styles.pwr} />
      <text x="56" y="45" className={styles.silk}>
        PWR
      </text>
      <circle cx="46" cy="58" r="3.5" className={styles.sys} />
      <text x="56" y="61" className={styles.silk}>
        SYS
      </text>

      {copper}
      {sfp}
    </svg>
  );
}
